import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Star, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { submitTestimonial } from '../lib/testimonials';
import { useToast } from '../hooks/use-toast';
import { trackTestimonialSubmission } from '../lib/analytics';
import SEOHead from '../components/SEOHead';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function SubmitTestimonial() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    location: '',
    content: '',
    rating: 5,
    highlight: '',
    email: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Testimonial content is required';
    } else if (formData.content.trim().length < 20) {
      newErrors.content = 'Please provide at least 20 characters';
    }

    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = 'Please select a rating';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: 'Please fix the errors',
        description: 'Some required fields are missing or invalid.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await submitTestimonial({
        ...formData,
        ipAddress: '' // Could be added server-side for security
      });

      // Track testimonial submission
      trackTestimonialSubmission();
      
      toast({
        title: 'Thank you!',
        description: 'Your testimonial has been submitted successfully. It will be reviewed and published soon.',
        variant: 'default'
      });

      // Reset form
      setFormData({
        name: '',
        role: '',
        location: '',
        content: '',
        rating: 5,
        highlight: '',
        email: ''
      });

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast({
        title: 'Submission failed',
        description: error.message || 'There was an error submitting your testimonial. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead 
        title="Share Your Experience | Grow & Stay Rich Client Reviews"
        description="Share your experience with Grow & Stay Rich. Submit your testimonial and help others discover our financial planning services."
        path="/submit-testimonial"
      />
      <Navigation />
      
      <div className="min-h-screen bg-gradient-to-br from-muted/50 to-background pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Share Your Experience
            </h1>
            <p className="text-xl text-muted-foreground">
              We'd love to hear about your experience with Grow and Stay Rich
            </p>
          </div>

          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Submit Your Testimonial</CardTitle>
              <CardDescription>
                Your feedback helps others make informed decisions about their financial future.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name - Required */}
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Your Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Role */}
                <div className="space-y-2">
                  <Label htmlFor="role">Your Role/Title (Optional)</Label>
                  <Input
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="e.g., Software Engineer, VP of Sales"
                  />
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="location">Location (Optional)</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="e.g., Dallas, TX"
                  />
                </div>

                {/* Rating - Required */}
                <div className="space-y-2">
                  <Label>
                    Rating <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => handleRatingClick(rating)}
                        className={`transition-all ${
                          rating <= formData.rating
                            ? 'text-yellow-400 scale-110'
                            : 'text-gray-300 hover:text-yellow-300'
                        }`}
                      >
                        <Star
                          className={`h-8 w-8 ${
                            rating <= formData.rating ? 'fill-current' : ''
                          }`}
                        />
                      </button>
                    ))}
                    <span className="ml-2 text-sm text-muted-foreground">
                      {formData.rating} out of 5
                    </span>
                  </div>
                  {errors.rating && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.rating}
                    </p>
                  )}
                </div>

                {/* Content - Required */}
                <div className="space-y-2">
                  <Label htmlFor="content">
                    Your Testimonial <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="Share your experience with Grow and Stay Rich. What services did you use? How did we help you? What would you tell others?"
                    rows={6}
                    className={errors.content ? 'border-red-500' : ''}
                  />
                  <p className="text-xs text-muted-foreground">
                    Minimum 20 characters. {formData.content.length} characters entered.
                  </p>
                  {errors.content && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.content}
                    </p>
                  )}
                </div>

                {/* Highlight */}
                <div className="space-y-2">
                  <Label htmlFor="highlight">Service/Category (Optional)</Label>
                  <Input
                    id="highlight"
                    name="highlight"
                    value={formData.highlight}
                    onChange={handleChange}
                    placeholder="e.g., Retirement Planning, Tax Strategies, Wealth Building"
                  />
                </div>

                {/* Email - Optional */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email (Optional - for verification only)</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.email}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Your email will not be displayed publicly. It's only used for verification purposes.
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Submit Testimonial
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/')}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>

                <p className="text-xs text-center text-muted-foreground pt-4">
                  By submitting, you agree that your testimonial may be published on our website.
                  All testimonials are reviewed before being published.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="mt-6 shadow-lg border-0 bg-gradient-to-r from-primary/10 to-secondary/10">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-2">What happens next?</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Your testimonial will be reviewed by our team</li>
                <li>• Approved testimonials will be published on our website</li>
                <li>• You'll receive a confirmation email (if provided)</li>
                <li>• Thank you for sharing your experience!</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </>
  );
}

